import type { DocumentItem } from "@/mocks/documentos";
import DocumentTypeBadge from "./DocumentTypeBadge";
import DocumentStatusBadge from "./DocumentStatusBadge";

interface DocumentCardProps {
  document: DocumentItem;
  onView: (doc: DocumentItem) => void;
}

const typeIconMap: Record<string, string> = {
  "FCA — Ficha Cadastral Arbórea": "ri-profile-line",
  "Laudo fitossanitário": "ri-microscope-line",
  "Parecer técnico": "ri-file-search-line",
  "Relatório de vistoria": "ri-clipboard-line",
  "Relatório fotográfico": "ri-camera-line",
  "Autorização ambiental": "ri-shield-check-line",
  "Ordem de serviço": "ri-tools-line",
  "Base cartográfica": "ri-map-line",
  "Shapefile": "ri-map-pin-line",
  "GeoJSON": "ri-map-2-line",
  "Mapa PDF": "ri-map-pin-line",
  "Ortofoto": "ri-image-line",
  "Evidência fotográfica": "ri-camera-line",
};

export default function DocumentCard({ document: doc, onView }: DocumentCardProps) {
  const typeIcon = typeIconMap[doc.type] || "ri-file-line";
  const isExpired = doc.status === "Vencido";

  return (
    <div className={`bg-white rounded-xl border ${isExpired ? "border-danger-200" : "border-background-200/70"} p-4 hover:shadow-sm transition-shadow`}>
      <div className="flex items-start gap-3 mb-3">
        <div className={`w-10 h-10 rounded-lg ${isExpired ? "bg-danger-50" : "bg-secondary-50"} flex items-center justify-center shrink-0`}>
          <i className={`${typeIcon} ${isExpired ? "text-danger-600" : "text-secondary-600"} w-full h-full flex items-center justify-center text-lg`} />
        </div>
        <div className="min-w-0 flex-1">
          <button onClick={() => onView(doc)} className="text-sm font-semibold text-foreground-800 hover:text-accent-600 transition-colors text-left line-clamp-2">
            {doc.title}
          </button>
          <div className="flex items-center gap-2 mt-1.5 flex-wrap">
            <DocumentTypeBadge type={doc.type} />
            <DocumentStatusBadge status={doc.status} />
          </div>
        </div>
      </div>

      <div className="space-y-2">
        {doc.projectName && (
          <div className="flex items-center gap-2 text-xs">
            <span className="text-foreground-400 shrink-0">Projeto:</span>
            <span className="text-foreground-700 font-medium truncate">{doc.projectName}</span>
          </div>
        )}
        <div className="flex items-center gap-2 text-xs">
          <span className="text-foreground-400 shrink-0">Vínculo:</span>
          <span className="text-foreground-700 font-medium truncate">{doc.primaryBinding}</span>
        </div>
        <div className="flex items-center gap-2 text-xs">
          <span className="text-foreground-400 shrink-0">Responsável:</span>
          <span className="text-foreground-600 truncate">{doc.responsible}</span>
        </div>
        <div className="flex items-center gap-2 text-xs">
          <span className="text-foreground-400 shrink-0">Emissão:</span>
          <span className="text-foreground-600">{doc.issueDate}</span>
          {doc.expiryDate && (
            <>
              <span className="text-foreground-300">|</span>
              <span className={`${isExpired ? "text-danger-600 font-semibold" : "text-foreground-600"}`}>Vence: {doc.expiryDate}</span>
            </>
          )}
        </div>
      </div>

      <div className="flex items-center gap-2 mt-3 pt-3 border-t border-background-100">
        <button onClick={() => onView(doc)} className="flex-1 px-3 py-1.5 text-xs font-semibold text-accent-700 bg-accent-50 rounded-md hover:bg-accent-100 transition-colors whitespace-nowrap">
          <i className="ri-eye-line w-3.5 h-3.5 inline-flex items-center justify-center mr-1" />
          Visualizar
        </button>
        <button className="px-3 py-1.5 text-xs font-semibold text-primary-700 bg-primary-50 rounded-md hover:bg-primary-100 transition-colors whitespace-nowrap">
          <i className="ri-link-m w-3.5 h-3.5 inline-flex items-center justify-center mr-1" />
          Vincular
        </button>
        {doc.hasFile && (
          <button className="px-3 py-1.5 text-xs font-semibold text-foreground-600 bg-background-50 rounded-md hover:bg-background-100 transition-colors whitespace-nowrap">
            <i className="ri-download-line w-3.5 h-3.5 inline-flex items-center justify-center mr-1" />
            Baixar
          </button>
        )}
      </div>
    </div>
  );
}